import React, { useRef, useState } from "react";
import type { FeedbackPage } from "@phoneware/lens-domain";

type Point = { x: number; y: number };
export type Mark =
  | { type: "box" | "arrow" | "element"; start: Point; end: Point }
  | { type: "freehand"; points: Point[] }
  | { type: "pin"; at: Point };
export type Tool = Mark["type"];
export interface PageCapture { blob: Blob; url: string; width: number; height: number }
export type Zoom = "fit" | "actual";

const BLANK_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Capture only this browser's visible Omni surface. No screenshot is made until someone asks. */
export async function capturePage(): Promise<PageCapture> {
  const root = document.querySelector<HTMLElement>(".app");
  if (!root) throw new Error("Omni page is not available to capture.");
  const width = Math.min(16384, Math.max(1, Math.floor(window.innerWidth)));
  const height = Math.min(16384, Math.max(1, Math.floor(window.innerHeight)));
  // A signed-in browser only downloads the DOM renderer after Capture this page; QuoteLink never needs it.
  const { domToCanvas } = await import("modern-screenshot");
  const canvas = await domToCanvas(root, {
    width, height, scale: 1,
    style: { height: `${height}px`, overflow: "hidden" },
    features: { restoreScrollPosition: true },
    fetchFn: async (url) => {
      try { return new URL(url, window.location.href).origin === window.location.origin ? false : BLANK_IMAGE; }
      catch { return BLANK_IMAGE; }
    },
  });
  const { promise, resolve, reject } = Promise.withResolvers<Blob>();
  canvas.toBlob((result) => result ? resolve(result) : reject(new Error("Could not save the screenshot.")), "image/png");
  const blob = await promise;
  return { blob, url: URL.createObjectURL(blob), width: canvas.width, height: canvas.height };
}

/** Positional selectors only. Neither text nor values nor ID/class attributes enter the request. */
export function structuralSelector(element: Element, root: Element): FeedbackPage["selectedElement"] {
  if (!root.contains(element)) return null;
  const parts: string[] = [];
  for (let node: Element | null = element; node && node !== root && parts.length < 12; node = node.parentElement) {
    const name = node.tagName.toLowerCase();
    let index = 1;
    for (let sibling = node.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
      if (sibling.tagName === node.tagName) index++;
    }
    parts.unshift(`${name}:nth-of-type(${index})`);
  }
  return { selector: parts.join(" > ").slice(0, 240), tagName: element.tagName.toLowerCase() };
}

function underlyingElement(point: Point, capture: PageCapture): { element: Element; bounds: DOMRect } | null {
  const root = document.querySelector<HTMLElement>(".app");
  if (!root) return null;
  const overlays = Array.from(document.querySelectorAll<HTMLElement>(".sheet, .scrim, .toast"));
  const previous = overlays.map((overlay) => overlay.style.pointerEvents);
  overlays.forEach((overlay) => { overlay.style.pointerEvents = "none"; });
  try {
    const x = point.x * window.innerWidth / capture.width;
    const y = point.y * window.innerHeight / capture.height;
    const element = document.elementsFromPoint(x, y).find((item) => root.contains(item) && item !== root);
    if (element) return { element, bounds: element.getBoundingClientRect() };
    // The Sheet makes the app inert while open. Some browsers omit inert elements
    // from hit testing, so find the deepest painted rect without reading its contents.
    let best: { element: Element; bounds: DOMRect; depth: number } | null = null;
    for (const candidate of root.querySelectorAll("*")) {
      const bounds = candidate.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0 || x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) continue;
      if (window.getComputedStyle(candidate).visibility !== "visible") continue;
      let depth = 0;
      for (let parent = candidate.parentElement; parent && parent !== root; parent = parent.parentElement) depth++;
      if (!best || depth > best.depth || (depth === best.depth && bounds.width * bounds.height < best.bounds.width * best.bounds.height)) {
        best = { element: candidate, bounds, depth };
      }
    }
    return best ? { element: best.element, bounds: best.bounds } : null;
  } finally {
    overlays.forEach((overlay, index) => { overlay.style.pointerEvents = previous[index]; });
  }
}

function markPath(mark: Mark, index: number): React.ReactNode {
  if (mark.type === "pin") return <g key={index}><circle cx={mark.at.x} cy={mark.at.y} r="11" fill="currentColor" /><circle cx={mark.at.x} cy={mark.at.y} r="3" fill="white" /></g>;
  if (mark.type === "freehand") return <polyline key={index} points={mark.points.map((point) => `${point.x},${point.y}`).join(" ")} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />;
  if (mark.type === "arrow") return <line key={index} x1={mark.start.x} y1={mark.start.y} x2={mark.end.x} y2={mark.end.y} stroke="currentColor" strokeWidth="3" markerEnd="url(#request-arrow)" />;
  return <rect key={index} x={Math.min(mark.start.x, mark.end.x)} y={Math.min(mark.start.y, mark.end.y)} width={Math.abs(mark.start.x - mark.end.x)} height={Math.abs(mark.start.y - mark.end.y)} fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={mark.type === "element" ? "8 5" : undefined} />;
}

export function CaptureEditor({ capture, marks, onMarks, tool, onSelection, zoom }: {
  capture: PageCapture; marks: Mark[]; onMarks: (marks: Mark[]) => void; tool: Tool;
  onSelection: (selected: FeedbackPage["selectedElement"]) => void; zoom: Zoom;
}): React.ReactElement {
  const stage = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const drag = useRef<{ pointerId: number; mark: Mark } | null>(null);
  const [preview, setPreview] = useState<Mark | null>(null);
  // Capture coordinates for a client point, measured against the image itself so a scrolled frame maps the same way.
  const toCapture = (clientX: number, clientY: number): Point => {
    const box = image.current?.getBoundingClientRect();
    if (!box || box.width <= 0 || box.height <= 0) return { x: capture.width / 2, y: capture.height / 2 };
    return {
      x: Math.max(0, Math.min(capture.width, (clientX - box.left) * capture.width / box.width)),
      y: Math.max(0, Math.min(capture.height, (clientY - box.top) * capture.height / box.height)),
    };
  };
  const at = (event: React.PointerEvent<HTMLDivElement>): Point => toCapture(event.clientX, event.clientY);
  // The middle of what the frame shows: the whole capture at Fit, the visible part of the image at Actual size.
  const visibleCenter = (): Point => {
    const box = image.current?.getBoundingClientRect();
    const view = viewport.current;
    if (zoom === "fit" || !box || !view) return { x: capture.width / 2, y: capture.height / 2 };
    const frame = view.getBoundingClientRect();
    const left = Math.max(box.left, frame.left + view.clientLeft);
    const top = Math.max(box.top, frame.top + view.clientTop);
    const right = Math.min(box.left + box.width, frame.left + view.clientLeft + view.clientWidth);
    const bottom = Math.min(box.top + box.height, frame.top + view.clientTop + view.clientHeight);
    return toCapture((left + right) / 2, (top + bottom) / 2);
  };
  const select = (point: Point): void => {
    const hit = underlyingElement(point, capture);
    const root = document.querySelector<HTMLElement>(".app");
    if (!hit || !root) return;
    onSelection(structuralSelector(hit.element, root));
    const x = window.innerWidth ? capture.width / window.innerWidth : 1;
    const y = window.innerHeight ? capture.height / window.innerHeight : 1;
    onMarks([...marks, { type: "element", start: { x: hit.bounds.left * x, y: hit.bounds.top * y }, end: { x: hit.bounds.right * x, y: hit.bounds.bottom * y } }]);
  };
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const point = at(event);
    if (tool === "pin") { onMarks([...marks, { type: "pin", at: point }]); return; }
    if (tool === "element") { select(point); return; }
    const mark: Mark = tool === "freehand" ? { type: "freehand", points: [point] } : { type: tool, start: point, end: point };
    drag.current = { pointerId: event.pointerId, mark };
    stage.current?.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (drag.current?.pointerId !== event.pointerId) return;
    const point = at(event);
    const mark = drag.current.mark;
    drag.current.mark = mark.type === "freehand" ? { ...mark, points: [...mark.points, point] } : mark.type === "pin" ? mark : { ...mark, end: point };
    setPreview(drag.current.mark);
  };
  const finish = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (drag.current?.pointerId !== event.pointerId) return;
    const point = at(event);
    const mark = drag.current.mark;
    const final = mark.type === "freehand" ? { ...mark, points: [...mark.points, point] } : mark.type === "pin" ? mark : { ...mark, end: point };
    drag.current = null;
    setPreview(null);
    onMarks([...marks, final]);
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    const center = visibleCenter();
    if (tool === "element") select(center);
    else if (tool === "pin") onMarks([...marks, { type: "pin", at: center }]);
    else if (tool === "freehand") onMarks([...marks, { type: "freehand", points: [center, { x: center.x + 16, y: center.y - 12 }, { x: center.x + 32, y: center.y }] }]);
    else onMarks([...marks, { type: tool, start: { x: center.x - 40, y: center.y - 25 }, end: { x: center.x + 40, y: center.y + 25 } }]);
  };
  return (
    <div ref={viewport} className="request-capture-viewport" data-zoom={zoom}
      style={{ "--capture-width": `${capture.width}px`, "--capture-ratio": `${capture.width} / ${capture.height}` } as React.CSSProperties}>
      <div ref={stage} className="request-capture-stage" role="img" tabIndex={0} aria-label="Screenshot of Omni, annotate with the selected tool"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={finish} onPointerCancel={finish} onKeyDown={onKeyDown}>
        <img ref={image} src={capture.url} alt="Screenshot of this Omni page" draggable={false} />
        <svg className="request-capture-overlay" viewBox={`0 0 ${capture.width} ${capture.height}`} preserveAspectRatio="none" aria-hidden="true">
          <defs><marker id="request-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0 0 8 4 0 8" fill="none" stroke="currentColor" strokeWidth="1.5" /></marker></defs>
          {marks.map(markPath)}{preview ? markPath(preview, -1) : null}
        </svg>
      </div>
    </div>
  );
}

function drawMark(ctx: CanvasRenderingContext2D, mark: Mark): void {
  ctx.strokeStyle = "#C6392F";
  ctx.fillStyle = "#C6392F";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (mark.type === "pin") {
    ctx.beginPath(); ctx.arc(mark.at.x, mark.at.y, 11, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#FFFFFF"; ctx.beginPath(); ctx.arc(mark.at.x, mark.at.y, 3, 0, Math.PI * 2); ctx.fill();
  } else if (mark.type === "freehand") {
    ctx.beginPath(); mark.points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y)); ctx.stroke();
  } else if (mark.type === "arrow") {
    ctx.beginPath(); ctx.moveTo(mark.start.x, mark.start.y); ctx.lineTo(mark.end.x, mark.end.y); ctx.stroke();
    const angle = Math.atan2(mark.end.y - mark.start.y, mark.end.x - mark.start.x);
    ctx.beginPath(); ctx.moveTo(mark.end.x, mark.end.y);
    ctx.lineTo(mark.end.x - 12 * Math.cos(angle - .55), mark.end.y - 12 * Math.sin(angle - .55));
    ctx.moveTo(mark.end.x, mark.end.y);
    ctx.lineTo(mark.end.x - 12 * Math.cos(angle + .55), mark.end.y - 12 * Math.sin(angle + .55)); ctx.stroke();
  } else {
    ctx.setLineDash(mark.type === "element" ? [8, 5] : []);
    ctx.strokeRect(Math.min(mark.start.x, mark.end.x), Math.min(mark.start.y, mark.end.y), Math.abs(mark.end.x - mark.start.x), Math.abs(mark.end.y - mark.start.y));
    ctx.setLineDash([]);
  }
}

export function captureOutputSize(width: number, height: number, maxDimension: number, maxPixels: number): { width: number; height: number } {
  const scale = Math.min(1, maxDimension / width, maxDimension / height, Math.sqrt(maxPixels / (width * height)));
  return { width: Math.max(1, Math.floor(width * scale)), height: Math.max(1, Math.floor(height * scale)) };
}

export async function annotatedScreenshot(
  capture: PageCapture,
  marks: Mark[],
  maxBytes: number,
  types: readonly ("image/png" | "image/webp")[],
  maxDimension: number,
  maxPixels: number,
): Promise<{ contentType: "image/png" | "image/webp"; dataBase64: string }> {
  const image = await createImageBitmap(capture.blob);
  try {
    const output = captureOutputSize(capture.width, capture.height, maxDimension, maxPixels);
    const width = output.width;
    const height = output.height;
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not save the screenshot.");
    ctx.scale(width / capture.width, height / capture.height);
    ctx.drawImage(image, 0, 0);
    marks.forEach((mark) => drawMark(ctx, mark));
    const encode = (contentType: "image/png" | "image/webp", quality?: number): Promise<Blob> => {
      const { promise, resolve, reject } = Promise.withResolvers<Blob>();
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not save the screenshot.")), contentType, quality);
      return promise;
    };
    let type: "image/png" | "image/webp" = types.includes("image/png") ? "image/png" : "image/webp";
    let blob = await encode(type);
    if (blob.size > maxBytes && types.includes("image/webp")) { type = "image/webp"; blob = await encode(type, .82); }
    if (blob.size > maxBytes) throw new Error("Screenshot is too large. Remove it to submit text only.");
    if (blob.type !== type) throw new Error("Could not save the screenshot in an accepted format.");
    const { promise, resolve, reject } = Promise.withResolvers<string>();
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(new Error("Could not save the screenshot."));
    reader.readAsDataURL(blob);
    const encoded = await promise;
    return { contentType: type, dataBase64: encoded };
  } finally { image.close(); }
}
